/**
 * useMyDataPort — port-factory hook returning a stable {@link MyDataPort}
 * adapter for production composition (033 US1, T024).
 *
 * The composer (`Composition`) calls this once at the root and passes the
 * instance through `PortsProvider` as the `myData` slot. Unlike the sibling
 * demo-backed member ports, this constructs the REAL tRPC-backed reader
 * (`TrpcMyDataReader` over `member.myData.view`) — the member's cookie
 * session authenticates the same-origin request, so no bearer source is
 * wired (the same posture as `useContentPort`).
 *
 * Test harnesses (`renderAppWithPorts`) deliberately do NOT call this
 * factory — the `myData` slot stays omitted by default so the missing-port
 * stub semantics hold; tests that exercise the surface pass a fixture port
 * explicitly.
 *
 * `useState(() => new TrpcMyDataReader(...))[0]`: the lazy initialiser runs
 * once per mount; the setter is discarded because the adapter holds no React
 * state (the established `useXyzPort` mechanism).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import { useState } from 'react';

import type { MyDataPort } from '../my-data/MyDataPort.js';
import { TrpcMyDataReader } from '../my-data/TrpcMyDataReader.js';
import { createWebTrpcClient } from '../trpc/client.js';

export function useMyDataPort(): MyDataPort {
  const [port] = useState<MyDataPort>(() => new TrpcMyDataReader(createWebTrpcClient()));
  return port;
}
